import {Component, EventEmitter, inject, Input, Output, ViewChild} from '@angular/core';
import {ContactStatusComponent} from "../../../../components";
import {
    DataSources,
    getSizeQualifier,
    TimeLogGridViewDto,
    ExceptionType,
    ScreenService,
    ClearExceptionsCommand,
    EmployeesClient, ShiftOptionDto
} from "@services";
import {
    DxButtonComponent,
    DxCheckBoxComponent,
    DxDateBoxComponent,
    DxFormComponent,
    DxPopupComponent,
    DxSelectBoxComponent, DxTextAreaComponent
} from "devextreme-angular";
import {DxiItemComponent, DxiValidationRuleComponent, DxoColCountByScreenComponent} from "devextreme-angular/ui/nested";
import {DasherizePipe} from "../../../../pipes/dasherize.pipe";
import {CapitalCasePipe} from "../../../../pipes/capital-case.pipe";
import {ToDatePipe} from "../../../../pipes/to-date.pipe";
import {AsyncPipe, DatePipe} from "@angular/common";
import {UserAvatarEmailComponent} from "../../../../components/utils/user-avatar-email/user-avatar-email.component";
import {DxiPopupToolbarItemComponent} from "devextreme-angular/ui/popup";
import notify from "devextreme/ui/notify";

interface ClearExceptionFormData {
    timeLog: TimeLogGridViewDto;
    isAbsent: boolean;
    actualStart: string;
    actualEnd: string;
}

@Component({
    selector: 'clear-exceptions',
    imports: [
        DxFormComponent,
        DxiItemComponent,
        DxiValidationRuleComponent,
        DxoColCountByScreenComponent,
        ContactStatusComponent,
        DasherizePipe,
        CapitalCasePipe,
        ToDatePipe,
        DatePipe,
        UserAvatarEmailComponent,
        AsyncPipe,
        DxButtonComponent,
        DxPopupComponent,
        DxiPopupToolbarItemComponent,
        DxSelectBoxComponent,
        DxCheckBoxComponent,
        DxDateBoxComponent,
        DxTextAreaComponent
    ],
    providers: [ToDatePipe, DatePipe],
    templateUrl: './clear-exceptions.component.html',
    styleUrls: ['./clear-exceptions.component.scss'],
})
export class ClearExceptionsComponent {
    private dataSources = inject(DataSources);
    private toDate = inject(ToDatePipe);
    private datePipe = inject(DatePipe);
    protected screen = inject(ScreenService);
    protected employeeClient = inject(EmployeesClient);
    protected ExceptionType = ExceptionType;

    @ViewChild(DxFormComponent, {static: false}) form: DxFormComponent;

    @Input() timeLog: TimeLogGridViewDto;
    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() onClear = new EventEmitter<void>();

    shiftDataSource = this.dataSources.shiftOptions();
    formData: ClearExceptionFormData | null = null;

    onShiftChanged = (e: any) => {
        const event = e as { name: string, value: ShiftOptionDto };
        if (event.name == "selectedItem" && this.formData) {
            this.formData.timeLog.shiftStartTime = event.value.startTime;
            this.formData.timeLog.shiftEndTime = event.value.endTime;

            if(!this.formData.actualStart)
                this.formData.actualStart = event.value.startTime;

            if(!this.formData.actualEnd)
                this.formData.actualEnd = event.value.endTime;
        }
    };

    get showStartSelect(): boolean {
        return this.timeLog?.exceptionType === ExceptionType.DoublePunch && (this.timeLog.punchInTimes?.length ?? 0) > 1;
    }

    get showEndSelect(): boolean {
        return this.timeLog?.exceptionType === ExceptionType.DoublePunch && (this.timeLog.punchOutTimes?.length ?? 0) > 1;
    }

    onSaveClick = () => {
        const validationResult = this.form.instance.validate();
        if (!validationResult || !validationResult.isValid) {
            return;
        }

        let clearExceptionsCommand = new ClearExceptionsCommand({
            timeLogId: this.formData.timeLog.id,
            shiftId: this.formData.timeLog.shiftId,
            shiftStart: this.formData.timeLog.shiftStartTime,
            shiftEnd: this.formData.timeLog.shiftEndTime,
            punchInTime: this.formData.actualStart,
            punchOutTime: this.formData.actualEnd,
            isAbsent: this.formData.isAbsent,
            reason: this.formData.timeLog.clearingExceptionReason,
        });

        this.employeeClient.clearExceptions(clearExceptionsCommand).subscribe({
            next: async () => {
                this.visible = false;
                this.visibleChange.emit(this.visible);
                this.onClear.emit();
            },
            error: (errors: Array<any>) => {
                errors.forEach((errMsg => {
                    notify({
                        message: errMsg.message,
                        type: "error",
                        displayTime: 5000,
                    }, {
                        position: "bottom right",
                        direction: "up-push"
                    });
                }));
            }
        });
    };

    protected readonly getSizeQualifier = getSizeQualifier;

    protected onOpen() {
        this.formData = {
            timeLog: {...this.timeLog} as TimeLogGridViewDto,
            isAbsent: false,
            actualStart: this.timeLog.firstPunchInTime,
            actualEnd: this.timeLog.firstPunchOutTime
        };
    }

    close() {
        this.formData = null;
        this.form.instance.reset();
        this.visible = false;
        this.visibleChange.emit(this.visible);
    }

    displayPunchTime = (item: any) => {
        if (!item) return '—';
        const d = this.toDate.transform(item);
        return d ? (this.datePipe.transform(d, 'h:mm a') ?? '') : '—';
    };
}
